import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

interface SignUpFormData {
  [key: string]: string // All fields are now dynamic
}

interface DynamicFormField {
  id: string
  fieldName: string
  fieldLabel: string
  fieldType: string
  isRequired: boolean
  placeholder?: string
  helpText?: string
  displayOrder: number
  validationRules?: any
}

const SignUpForm: React.FC = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState<SignUpFormData>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dynamicFields, setDynamicFields] = useState<DynamicFormField[]>([])
  const [fieldsLoading, setFieldsLoading] = useState(true)
  const [currentStep, setCurrentStep] = useState(0)
  const [fieldGroups, setFieldGroups] = useState<DynamicFormField[][]>([])
  const [isAnimating, setIsAnimating] = useState(false)
  const [animationDirection, setAnimationDirection] = useState<'forward' | 'backward'>('forward')

  // Group fields into steps (1 field per step)
  const groupFieldsIntoSteps = (fields: DynamicFormField[]) => {
    const sortedFields = fields.sort((a, b) => a.displayOrder - b.displayOrder)
    const groups: DynamicFormField[][] = []
    
    // One field per step
    for (const field of sortedFields) {
      groups.push([field])
    }
    
    return groups
  }

  // Load dynamic form fields on component mount
  useEffect(() => {
    const loadFormFields = async () => {
      try {
        const response = await fetch('/api/invitations/form-fields')
        if (response.ok) {
          const data = await response.json()
          const fields = data.data || []
          setDynamicFields(fields)
          setFieldGroups(groupFieldsIntoSteps(fields))
        } else {
          console.error('Failed to load form fields:', response.status)
        }
      } catch (error) {
        console.error('Error loading form fields:', error)
      } finally {
        setFieldsLoading(false)
      }
    }

    loadFormFields()
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (error) setError(null)
  }

  const validateForm = (): string | null => {
    // Validate all dynamic fields
    for (const field of dynamicFields) {
      const value = formData[field.fieldName] || ''
      
      if (field.isRequired && !value.trim()) {
        return `${field.fieldLabel} is required`
      }
      
      // Basic type validation
      if (value && field.fieldType === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(value)) {
          return `Please enter a valid email address for ${field.fieldLabel}`
        }
      }
      
      if (value && field.fieldType === 'phone') {
        const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
        if (!phoneRegex.test(value.replace(/[\s\-\(\)\.]/g, ''))) {
          return `Please enter a valid phone number for ${field.fieldLabel}`
        }
      }
      
      if (value && field.fieldType === 'number') {
        if (isNaN(Number(value))) {
          return `${field.fieldLabel} must be a valid number`
        }
      }
      
      // Additional validation rules if any
      if (value && field.validationRules) {
        const rules = field.validationRules
        
        if (rules.min_length && value.length < rules.min_length) {
          return `${field.fieldLabel} must be at least ${rules.min_length} characters long`
        }
        
        if (rules.max_length && value.length > rules.max_length) {
          return `${field.fieldLabel} must be no more than ${rules.max_length} characters long`
        }
        
        if (rules.pattern) {
          const regex = new RegExp(rules.pattern)
          if (!regex.test(value)) {
            return `${field.fieldLabel} format is invalid`
          }
        }
      }
    }

    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Don't auto-submit, only submit when explicitly called
    return
  }

  const handleFormSubmit = async () => {
    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await fetch('/api/invitations/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (response.ok) {
        setSubmitted(true)
      } else {
        setError(data.message || 'An error occurred while submitting your request')
      }
    } catch (err) {
      console.error('Sign-up request error:', err)
      setError('Unable to submit your request. Please check your internet connection and try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBackToHome = () => {
    navigate('/')
  }

  const handleNextStep = () => {
    const currentField = fieldGroups[currentStep]?.[0]
    
    if (currentField) {
      const value = formData[currentField.fieldName] || ''
      
      if (currentField.isRequired && !value.trim()) {
        setError(`${currentField.fieldLabel} is required`)
        return
      }
      
      // Basic validation for field types
      if (value && currentField.fieldType === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(value)) {
          setError(`Please enter a valid email address`)
          return
        }
      }
      
      if (value && currentField.fieldType === 'phone') {
        const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
        if (!phoneRegex.test(value.replace(/[\s\-\(\)\.]/g, ''))) {
          setError(`Please enter a valid phone number`)
          return
        }
      }
    }
    
    setError(null)
    if (currentStep < fieldGroups.length - 1) {
      setIsAnimating(true)
      setAnimationDirection('forward')
      
      setTimeout(() => {
        setCurrentStep(currentStep + 1)
        setTimeout(() => {
          setIsAnimating(false)
        }, 50)
      }, 150)
    }
  }

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setIsAnimating(true)
      setAnimationDirection('backward')
      
      setTimeout(() => {
        setCurrentStep(currentStep - 1)
        setError(null)
        setTimeout(() => {
          setIsAnimating(false)
        }, 50)
      }, 150)
    }
  }

  // Check if we should use two-column layout for specific field combinations
  const shouldUseTwoColumns = (field: DynamicFormField, index: number) => {
    if (index >= dynamicFields.length - 1) return false
    const nextField = dynamicFields[index + 1]
    
    // Use two columns for name-like fields that are adjacent
    const nameFields = ['first_name', 'last_name', 'contact_first_name', 'contact_last_name']
    const locationFields = ['city', 'state', 'country', 'zip_code', 'postal_code']
    
    return (
      (nameFields.includes(field.fieldName) && nameFields.includes(nextField.fieldName)) ||
      (locationFields.includes(field.fieldName) && locationFields.includes(nextField.fieldName)) ||
      (field.fieldType === 'text' && nextField.fieldType === 'text' && 
       field.fieldName.includes('first') && nextField.fieldName.includes('last'))
    )
  }

  // Render current step field (single field)
  const renderCurrentStep = () => {
    if (fieldsLoading) {
      return (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading form fields...</span>
          </div>
          <div className="mt-3">
            <p className="text-muted">Loading form fields...</p>
          </div>
        </div>
      )
    }

    if (fieldGroups.length === 0) {
      return (
        <div className="text-center my-5">
          <i className="bi bi-info-circle text-muted" style={{ fontSize: '3rem' }}></i>
          <h4 className="text-muted mt-3">No Form Fields Available</h4>
          <p className="text-muted">
            Please contact the administrator to configure signup form fields.
          </p>
        </div>
      )
    }

    const currentField = fieldGroups[currentStep]?.[0]
    if (!currentField) return null
    
    return renderSingleField(currentField)
  }

  // Render single field with better visibility
  const renderSingleField = (field: DynamicFormField) => {
    const value = formData[field.fieldName] || ''
    const fieldId = `dynamic_${field.fieldName}`
    
    const commonProps = {
      id: fieldId,
      name: field.fieldName,
      value: value,
      onChange: handleInputChange,
      required: field.isRequired,
      disabled: isSubmitting,
      placeholder: field.placeholder || `Enter ${field.fieldLabel.toLowerCase()}`,
      className: "form-control",
      style: { 
        borderRadius: '16px',
        border: '3px solid #e5e7eb',
        padding: '24px 28px',
        fontSize: '18px',
        fontWeight: '500',
        backgroundColor: '#ffffff',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.2s ease-in-out',
        minHeight: '70px'
      },
      onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        e.target.style.borderColor = '#02225b'
        e.target.style.boxShadow = '0 0 0 3px rgba(2, 34, 91, 0.1), 0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      },
      onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        e.target.style.borderColor = '#e5e7eb'
        e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      }
    }

    let inputElement
    switch (field.fieldType) {
      case 'textarea':
        inputElement = <textarea {...commonProps} rows={4} style={{ ...commonProps.style, minHeight: '120px' }} />
        break
      case 'number':
        inputElement = <input {...commonProps} type="number" />
        break
      case 'email':
        inputElement = <input {...commonProps} type="email" />
        break
      case 'phone':
        inputElement = <input {...commonProps} type="tel" />
        break
      case 'date':
        inputElement = <input {...commonProps} type="date" />
        break
      case 'select':
        inputElement = <input {...commonProps} type="text" />
        break
      case 'text':
      default:
        inputElement = <input {...commonProps} type="text" />
        break
    }

    return (
      <div 
        className="mb-4"
        style={{
          transform: isAnimating 
            ? `translateX(${animationDirection === 'forward' ? '30px' : '-30px'})` 
            : 'translateX(0)',
          opacity: isAnimating ? 0.3 : 1,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        {inputElement}
      </div>
    )
  }

  // Render individual field as a card
  const renderFieldCard = (field: DynamicFormField, index: number) => {
    const value = formData[field.fieldName] || ''
    const fieldId = `dynamic_${field.fieldName}`
    
    const commonProps = {
      id: fieldId,
      name: field.fieldName,
      value: value,
      onChange: handleInputChange,
      required: field.isRequired,
      disabled: isSubmitting,
      placeholder: field.placeholder || `Enter ${field.fieldLabel.toLowerCase()}`,
      className: "form-control form-control-lg border-0",
      style: { 
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        padding: '20px',
        fontSize: '16px',
        boxShadow: 'none'
      }
    }

    let inputElement
    switch (field.fieldType) {
      case 'textarea':
        inputElement = <textarea {...commonProps} rows={3} />
        break
      case 'number':
        inputElement = <input {...commonProps} type="number" />
        break
      case 'email':
        inputElement = <input {...commonProps} type="email" />
        break
      case 'phone':
        inputElement = <input {...commonProps} type="tel" />
        break
      case 'date':
        inputElement = <input {...commonProps} type="date" />
        break
      case 'select':
        inputElement = <input {...commonProps} type="text" />
        break
      case 'text':
      default:
        inputElement = <input {...commonProps} type="text" />
        break
    }

    return (
      <div key={field.id} className="col-12">
        <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', transition: 'all 0.2s ease-in-out' }}>
          <div className="card-body p-4">
            <div className="d-flex align-items-center mb-3">
              <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                <i className="bi bi-person-fill text-white" style={{ fontSize: '1.2rem' }}></i>
              </div>
              <div className="flex-grow-1">
                <h5 className="mb-1 fw-semibold" style={{ color: '#374151' }}>
                  {field.fieldLabel}
                  {field.isRequired && <span className="text-danger"> *</span>}
                </h5>
                {field.helpText && (
                  <p className="text-muted mb-0 small">{field.helpText}</p>
                )}
              </div>
              <i className="bi bi-arrow-right text-muted"></i>
            </div>
            {inputElement}
          </div>
        </div>
      </div>
    )
  }

  // Render dynamic form field based on its type (keeping for compatibility)
  const renderDynamicField = (field: DynamicFormField, index: number) => {
    const value = formData[field.fieldName] || ''
    const fieldId = `dynamic_${field.fieldName}`
    const isFirstOfPair = shouldUseTwoColumns(field, index)
    const isSecondOfPair = index > 0 && shouldUseTwoColumns(dynamicFields[index - 1], index - 1)
    
    // Skip rendering if this is the second field in a pair (it's rendered with the first)
    if (isSecondOfPair) return null
    
    const commonProps = {
      id: fieldId,
      name: field.fieldName,
      value: value,
      onChange: handleInputChange,
      required: field.isRequired,
      disabled: isSubmitting,
      placeholder: field.placeholder || `Enter ${field.fieldLabel.toLowerCase()}`,
      className: "form-control form-control-lg",
      style: { 
        borderRadius: '12px', 
        border: '2px solid #e9ecef',
        padding: '16px 20px',
        fontSize: '16px',
        transition: 'all 0.2s ease-in-out'
      }
    }

    let inputElement
    switch (field.fieldType) {
      case 'textarea':
        inputElement = <textarea {...commonProps} rows={3} />
        break
      case 'number':
        inputElement = <input {...commonProps} type="number" />
        break
      case 'email':
        inputElement = <input {...commonProps} type="email" />
        break
      case 'phone':
        inputElement = <input {...commonProps} type="tel" />
        break
      case 'date':
        inputElement = <input {...commonProps} type="date" />
        break
      case 'select':
        // For now, render as text input - would need picklist values for proper select
        inputElement = <input {...commonProps} type="text" />
        break
      case 'text':
      default:
        inputElement = <input {...commonProps} type="text" />
        break
    }

    if (isFirstOfPair) {
      const nextField = dynamicFields[index + 1]
      const nextValue = formData[nextField.fieldName] || ''
      const nextFieldId = `dynamic_${nextField.fieldName}`
      
      const nextCommonProps = {
        id: nextFieldId,
        name: nextField.fieldName,
        value: nextValue,
        onChange: handleInputChange,
        required: nextField.isRequired,
        disabled: isSubmitting,
        placeholder: nextField.placeholder || `Enter ${nextField.fieldLabel.toLowerCase()}`,
        className: "form-control form-control-lg",
        style: { 
          borderRadius: '12px', 
          border: '2px solid #e9ecef',
          padding: '16px 20px',
          fontSize: '16px',
          transition: 'all 0.2s ease-in-out'
        }
      }

      let nextInputElement
      switch (nextField.fieldType) {
        case 'number':
          nextInputElement = <input {...nextCommonProps} type="number" />
          break
        case 'email':
          nextInputElement = <input {...nextCommonProps} type="email" />
          break
        case 'phone':
          nextInputElement = <input {...nextCommonProps} type="tel" />
          break
        case 'date':
          nextInputElement = <input {...nextCommonProps} type="date" />
          break
        case 'text':
        default:
          nextInputElement = <input {...nextCommonProps} type="text" />
          break
      }

      return (
        <div key={field.id} className="row mb-3">
          <div className="col-md-6">
            <label htmlFor={fieldId} className="form-label fw-semibold mb-2" style={{ fontSize: '15px', color: '#374151' }}>
              {field.fieldLabel}
              {field.isRequired && <span className="text-danger"> *</span>}
            </label>
            {inputElement}
            {field.helpText && (
              <div className="form-text">{field.helpText}</div>
            )}
          </div>
          <div className="col-md-6">
            <label htmlFor={nextFieldId} className="form-label fw-semibold mb-2" style={{ fontSize: '15px', color: '#374151' }}>
              {nextField.fieldLabel}
              {nextField.isRequired && <span className="text-danger"> *</span>}
            </label>
            {nextInputElement}
            {nextField.helpText && (
              <div className="form-text">{nextField.helpText}</div>
            )}
          </div>
        </div>
      )
    }

    return (
      <div key={field.id} className="mb-3">
        <label htmlFor={fieldId} className="form-label fw-semibold mb-2" style={{ fontSize: '15px', color: '#374151' }}>
          {field.fieldLabel}
          {field.isRequired && <span className="text-danger"> *</span>}
        </label>
        {inputElement}
        {field.helpText && (
          <div className="form-text">{field.helpText}</div>
        )}
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="w-100" style={{ maxWidth: '600px', padding: '20px' }}>
          <div 
            className="card border-0" 
            style={{ 
              borderRadius: '20px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div className="border shadow border-rounded card-body text-center p-5">
              {/* Success Icon */}
              <div className="mb-4">
                <div 
                  className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
                  style={{ 
                    width: '80px', 
                    height: '80px',
                    background: 'linear-gradient(135deg, #02225b 0%, #011a42 100%)'
                  }}
                >
                  <i className="bi bi-check" style={{ fontSize: '2.5rem', color: 'white', fontWeight: 'bold' }}></i>
                </div>
              </div>
              
              {/* Success Header */}
              <h1 className="fw-bold mb-3" style={{ fontSize: '2rem', color: '#02225b' }}>
                Request Submitted Successfully!
              </h1>
              <p className="text-muted mb-5" style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                Thank you for your interest in joining the ISO Portal. Your sign-up request has been submitted and is now being reviewed by our team.
              </p>
              
              {/* Next Steps Card */}
              <div 
                className="card border-0 mb-4"
                style={{ 
                  backgroundColor: '#f1f3f4',
                  borderRadius: '16px'
                }}
              >
                <div className="card-body p-4">
                  <h5 className="fw-semibold mb-4" style={{ color: '#02225b', textAlign: 'center' }}>
                    What happens next?
                  </h5>
                  
                  <div className="text-start">
                    <div className="mb-3">
                      <div className="d-flex align-items-start">
                        <div 
                          className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                          style={{ 
                            width: '32px', 
                            height: '32px',
                            backgroundColor: '#02225b',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        >
                          1
                        </div>
                        <div>
                          <h6 className="mb-1 fw-semibold" style={{ color: '#02225b' }}>Application Review</h6>
                          <p className="mb-0 text-muted small">Our admin team will review your application</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <div className="d-flex align-items-start">
                        <div 
                          className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                          style={{ 
                            width: '32px', 
                            height: '32px',
                            backgroundColor: '#02225b',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        >
                          2
                        </div>
                        <div>
                          <h6 className="mb-1 fw-semibold" style={{ color: '#02225b' }}>Email Notification</h6>
                          <p className="mb-0 text-muted small">You'll receive an email notification with our decision</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <div className="d-flex align-items-start">
                        <div 
                          className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                          style={{ 
                            width: '32px', 
                            height: '32px',
                            backgroundColor: '#02225b',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        >
                          3
                        </div>
                        <div>
                          <h6 className="mb-1 fw-semibold" style={{ color: '#02225b' }}>Account Setup</h6>
                          <p className="mb-0 text-muted small">If approved, you'll get a secure link to complete your account setup</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-0">
                      <div style={{ marginLeft: '47px' }}>
                        <h6 className="mb-1 fw-semibold" style={{ color: '#02225b' }}>Timeline</h6>
                        <p className="mb-0 text-muted small">The review process typically takes 1-2 business days</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Return Button */}
              <button 
                className="btn btn-lg px-5"
                onClick={handleBackToHome}
                style={{
                  background: 'linear-gradient(135deg, #02225b 0%, #011a42 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  color: 'white',
                  fontWeight: '600',
                  padding: '12px 32px'
                }}
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="vh-100 d-flex">
      {/* Left Sidebar - Brand dark blue */}
      <div className="d-none d-lg-flex flex-column justify-content-center align-items-start text-white px-5" style={{ 
        flex: '0 0 40%', 
        background: 'linear-gradient(135deg, #02225b 0%, #011a42 100%)',
        position: 'relative'
      }}>
        {/* Logo at top */}
        <div className="position-absolute top-0 start-0 p-4">
          <div className="d-flex align-items-center">
            <div className="rounded-circle bg-white d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px' }}>
              <span className="fw-bold text-primary" style={{ fontSize: '14px' }}>F</span>
            </div>
            <span className="fw-bold text-white">FundMerica</span>
          </div>
        </div>

        {/* Help link at top right */}
        <div className="position-absolute top-0 end-0 p-4">
          <span className="text-white" style={{ opacity: '0.8', fontSize: '14px' }}>
            Having troubles? <a href="#" className="text-white fw-semibold text-decoration-none">Get Help</a>
          </span>
        </div>
        
        {/* Main content */}
        <div className="mb-5">
          <h1 className="fw-bold mb-4 text-white" style={{ fontSize: '3rem', lineHeight: '1.1' }}>
            A few clicks away from becoming a partnered ISO.
          </h1>
          <p className="text-white mb-3" style={{ fontSize: '1.1rem', opacity: '0.9' }}>
            Start sending deals and earning commissions in minutes.
          </p>
         
        </div>
        
        {/* Illustration placeholder */}
        <div className="position-absolute bottom-0 end-0 p-4" style={{ opacity: '0.3' }}>
          <div style={{ width: '120px', height: '120px', background: 'rgba(255,255,255,0.1)', borderRadius: '8px' }}></div>
        </div>
      </div>

      {/* Right Form Section */}
      <div className="flex-fill d-flex align-items-center justify-content-center px-4" style={{ backgroundColor: '#ffffff' }}>
        <div className="w-100" style={{ maxWidth: '600px' }}>
          <div className="text-center mb-5">
            {/* Progress Circles */}
            {fieldGroups.length > 0 && (
              <div className="d-flex justify-content-center align-items-center mb-4">
                {fieldGroups.map((_, index) => (
                  <React.Fragment key={index}>
                    <div 
                      className={`rounded-circle d-flex align-items-center justify-content-center ${
                        index <= currentStep ? 'text-white' : 'bg-light border text-muted'
                      }`}
                      style={{ 
                        width: '32px', 
                        height: '32px',
                        fontSize: '14px',
                        fontWeight: '600',
                        transition: 'all 0.3s ease-in-out',
                        transform: index === currentStep ? 'scale(1.1)' : 'scale(1)',
                        backgroundColor: index <= currentStep ? '#02225b' : undefined
                      }}
                    >
                      {index < currentStep ? (
                        <i className="bi bi-check" style={{ fontSize: '16px' }}></i>
                      ) : (
                        index + 1
                      )}
                    </div>
                    {index < fieldGroups.length - 1 && (
                      <div 
                        className="mx-3"
                        style={{ 
                          width: '40px', 
                          height: '2px', 
                          backgroundColor: index < currentStep ? '#02225b' : '#dee2e6',
                          transition: 'background-color 0.3s ease-in-out'
                        }}
                      ></div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
            
            <div
              style={{
                transform: isAnimating 
                  ? `translateY(${animationDirection === 'forward' ? '-10px' : '10px'})` 
                  : 'translateY(0)',
                opacity: isAnimating ? 0.5 : 1,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <h1 className="fw-bold mb-3" style={{ fontSize: '1.8rem', color: '#374151' }}>
                {fieldGroups.length > 0 && currentStep < fieldGroups.length 
                  ? fieldGroups[currentStep][0]?.fieldLabel || 'Complete Your Application'
                  : 'Complete Your Application'}
              </h1>
              <p className="text-muted mb-4" style={{ fontSize: '1rem' }}>
                {fieldGroups.length > 0 && currentStep < fieldGroups.length 
                  ? fieldGroups[currentStep][0]?.helpText || 'Please provide the required information to continue.'
                  : 'Creating your account is just a few steps away.'}
              </p>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger border-0 mb-4" role="alert" style={{ borderRadius: '12px' }}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Current Step Field - Centered */}
            <div className="d-flex justify-content-center mb-3">
              <div style={{ width: '100%', maxWidth: '500px' }}>
                {renderCurrentStep()}
              </div>
            </div>
            
            {/* Navigation Buttons */}
            {fieldGroups.length > 0 && (
              <div className="d-flex justify-content-center">
                <div className="d-flex justify-content-between mt-2" style={{ width: '100%', maxWidth: '500px' }}>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handlePrevStep}
                  disabled={currentStep === 0 || isSubmitting}
                  style={{
                    borderRadius: '12px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '500',
                    minWidth: '120px'
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Previous
                </button>
                
                {currentStep < fieldGroups.length - 1 ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleNextStep}
                    disabled={isSubmitting}
                    style={{
                      borderRadius: '12px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: '600',
                      border: 'none',
                      background: 'linear-gradient(135deg, #02225b 0%, #011a42 100%)',
                      minWidth: '120px'
                    }}
                  >
                    Continue
                    <i className="bi bi-arrow-right ms-2"></i>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleFormSubmit}
                    disabled={isSubmitting}
                    style={{
                      borderRadius: '12px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: '600',
                      border: 'none',
                      background: 'linear-gradient(135deg, #02225b 0%, #011a42 100%)',
                      minWidth: '120px'
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit
                        <i className="bi bi-check ms-2"></i>
                      </>
                    )}
                  </button>
                )}
                </div>
              </div>
            )}
          </form>

          {/* Footer Links */}
          <div className="d-flex justify-content-center mt-5">
            <div className="d-flex justify-content-between align-items-center" style={{ width: '100%', maxWidth: '500px' }}>
              <button
                type="button"
                className="btn btn-link text-decoration-none p-0 text-muted"
                onClick={handleBackToHome}
                disabled={isSubmitting}
                style={{ fontSize: '14px' }}
              >
                ← Back to Home
              </button>
              <a href="/login" className="text-decoration-none text-muted" style={{ fontSize: '14px' }}>
                Already have an account? Sign in
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignUpForm